import { Location } from "@angular/common";
import { Injectable, OnDestroy } from "@angular/core";
import { NavigationExtras, Router } from "@angular/router";
import { SubSink } from "subsink";
import { AuthorizationService } from "../authorization/services/authorization.service";
import { CirculationId, EntityId, IMetaData, StepId } from "../interfaces";
import { Constants } from "../interfaces/constants";
import { UserFacade } from "../user/services/user.facade";

@Injectable({
  providedIn: "root"
})
export class NavigationService implements OnDestroy {
  /**
   * this service has two purposes:
   * 1. provide "nice" routing (i.e. the urls are not "everywhere")
   * 2. ensure queryParamsHandling is always (!) set!
   */
  private extras: NavigationExtras = { queryParamsHandling: "preserve" };
  private url = {
    admin: Constants.routePaths.admin,
    circ: "circ",
    dashboard: "",
    edit: "edit",
    error: "error",
    step: "step",
    new: "new",
    meta: "meta",
    id: "id",
    templates: "templates",
    tasks: "tasks",
    user: "user",
    roles: "roles",
    users: Constants.routePaths.users,
    substitutes: Constants.routePaths.substitutes
  };
  private subsink = new SubSink();
  constructor(
    private location: Location,
    private router: Router,
    private authorizationSerivce: AuthorizationService,
    private userFacade: UserFacade
  ) {}
  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  public back(): void {
    this.location.back();
  }

  public to(
    pathSegments: (string | number)[],
    extras?: NavigationExtras
  ): void {
    if (!extras) {
      extras = this.extras;
    } else {
      extras = {
        ...this.extras,
        ...extras
      };
    }
    this.subsink.sink = this.userFacade.getCurrentUser().subscribe((x) => {
      if (this.authorizationSerivce.isUnknown(x.roles)) {
        this.router.navigate([this.url.error], {
          replaceUrl: true,
          state: {
            message: "Unknown User! Please ask your administrator for help."
          }
        });
      } else {
        this.router.navigate(pathSegments, extras);
      }
    });
  }

  public toFullUrl(url: string): void {
    this.subsink.sink = this.userFacade.getCurrentUser().subscribe((x) => {
      if (this.authorizationSerivce.isUnknown(x.roles)) {
        this.router.navigate([this.url.error], {
          replaceUrl: true,
          state: {
            message: "Unknown User! Please ask your administrator for help."
          }
        });
      } else {
        console.log("Navigating to full URL:", url);
        this.router.navigateByUrl(url);
      }
    });
  }

  public toBackgroundJobs(): void {
    const url = `${window.location.origin}/dashboard`;
    window.open(url, "_blank");
  }

  public toAdminDashboard(): void {
    this.to([this.url.admin]);
  }

  public toAdminSubstitutes(): void {
    this.to([this.url.admin, this.url.substitutes]);
  }

  public toAdminRoles(): void {
    this.to([this.url.admin, this.url.roles]);
  }

  public toAdminSubstituteEdit(userId: EntityId): void {
    this.to([this.url.admin, this.url.substitutes, this.url.user, userId]);
  }

  public toAdminUserRoleEdit(userId: EntityId): void {
    this.to([this.url.admin, this.url.users, this.url.id, userId]);
  }

  public toUserSubstitute(): void {
    this.to([this.url.substitutes]);
  }

  public toAdminUsers(): void {
    this.to([this.url.admin, this.url.users]);
  }

  public toAdminSettings(): void {
    this.to([this.url.admin, "settings"]);
  }

  public toCirculationOverview(): void {
    this.to([this.url.circ]);
  }

  public toCirculation(circId: CirculationId): void {
    this.to([this.url.circ, this.url.id, circId]);
  }

  public toCirculationCreation(): void {
    this.to([this.url.circ, this.url.new]);
  }

  public toCirculationEdit(circId: CirculationId): void {
    this.to([this.url.circ, this.url.id, circId, this.url.edit]);
  }

  public toCirculationCreationFromTemplate(): void {
    this.to([this.url.circ, "newFromTemplate"]);
  }

  public toCirculationStartMultiple(): void {
    this.to([this.url.circ, "startMultiple"]);
  }

  public toDashboard(): void {
    this.to([this.url.dashboard]);
  }

  public toError(error: string): void {
    this.to([this.url.error], {
      replaceUrl: true,
      state: { message: error }
    });
  }

  public toStep(circId: CirculationId, id: StepId): void {
    this.to([this.url.step, circId, this.url.id, id]);
  }

  public toStepCreation(circId: CirculationId): void {
    this.to([this.url.step, circId, this.url.new]);
  }

  public toStepEdit(circId: CirculationId, id: StepId): void {
    this.to([this.url.step, circId, this.url.id, id, this.url.edit]);
  }

  public toStepForward(circId: CirculationId, id: StepId): void {
    this.to([this.url.step, circId, this.url.id, id, "forward"]);
  }

  public toStepOverride(circId: CirculationId, id: StepId): void {
    this.to([this.url.step, circId, this.url.id, id, "override"]);
  }

  public toStepReorder(circId: CirculationId): void {
    this.to([this.url.circ, this.url.id, circId, "reorderSteps"]);
  }

  public toStepWork(circId: CirculationId, id: StepId): void {
    this.to([this.url.step, circId, this.url.id, id]);
  }

  public toTasks(): void {
    this.to([this.url.tasks]);
  }

  public toTemplateCreation(): void {
    this.to([this.url.templates, this.url.new]);
  }

  public toTemplates(): void {
    this.to([this.url.templates]);
  }

  public toTemplate(templateId: CirculationId): void {
    this.to([this.url.templates, templateId]);
  }

  public toTemplateEdit(templateId: CirculationId): void {
    this.to([this.url.templates, this.url.edit, templateId]);
  }

  public toTemplateStepCreation(templateId: CirculationId): void {
    this.to([this.url.templates, templateId, this.url.step, this.url.new]);
  }

  public toTemplateStepEdit(templateId: CirculationId, stepId: number): void {
    this.to([this.url.templates, templateId, this.url.step, stepId]);
  }

  public toMetaDataCreation(templateId: CirculationId): void {
    this.to([this.url.templates, templateId, this.url.meta, this.url.new]);
  }

  public toMetaDataEdit(templateId: CirculationId, data: IMetaData): void {
    this.to([this.url.templates, templateId, this.url.meta, data.key]);
  }
}
